import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, Loader2, Clock } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

export default function PaymentSuccess() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("verifying");

  useEffect(() => {
    if (!sessionId) {
      setState("pending");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid" || data.status === "completed") {
          setState("paid");
          return;
        }
      } catch (e) {
        // keep polling
      }
      attempts += 1;
      if (attempts < 15) {
        timer = setTimeout(poll, 2000);
      } else {
        setState("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <main data-testid="payment-success-page">
      <Header />
      <div className="pt-44 pb-32 max-w-xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="rounded-3xl border border-mint/25 bg-gradient-to-br from-mint/10 via-card to-night p-12"
        >
          {state === "verifying" && (
            <div data-testid="payment-status-verifying">
              <Loader2 className="w-12 h-12 text-mint mx-auto animate-spin" strokeWidth={1.5} />
              <h1 className="mt-6 font-display font-semibold text-white text-2xl">{t("payment.verifying")}</h1>
            </div>
          )}
          {state === "paid" && (
            <div data-testid="payment-status-success">
              <CheckCircle2 className="w-12 h-12 text-mint mx-auto" strokeWidth={1.5} />
              <h1 className="mt-6 font-display font-semibold text-white text-2xl">{t("payment.successTitle")}</h1>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">{t("payment.successText")}</p>
            </div>
          )}
          {state === "pending" && (
            <div data-testid="payment-status-pending">
              <Clock className="w-12 h-12 text-amber-400 mx-auto" strokeWidth={1.5} />
              <h1 className="mt-6 font-display font-semibold text-white text-2xl">{t("payment.pendingTitle")}</h1>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">{t("payment.pendingText")}</p>
            </div>
          )}
          <Link
            to="/"
            data-testid="payment-success-home-link"
            className="mt-8 inline-flex items-center rounded-full bg-mint px-6 py-3 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105"
          >
            {t("payment.backHome")}
          </Link>
        </motion.div>
      </div>
      <Footer />
    </main>
  );
}
